<script lang="ts">
  import { twMerge } from 'tailwind-merge';

  interface Props {
    value?: string | null;
    displayValue?: string | null;
    rows?: number;
    placeholder?: string;
    disabled?: boolean;
    valid?: boolean;
    label?: string | null;
    tall?: boolean;
    name?: string;
    class?: string | null;
    onFocusOut?: () => void;
    onEdit?: () => void;
    dataTestId?: string;
    // These five replace bare `on:blur` / `on:input` / … forwards.
    onblur?: (event: FocusEvent) => void;
    oninput?: (event: Event) => void;
    onkeyup?: (event: KeyboardEvent) => void;
    onkeydown?: (event: KeyboardEvent) => void;
    onkeypress?: (event: KeyboardEvent) => void;
  }

  let {
    value = $bindable(),
    displayValue = null,
    rows = 5,
    placeholder = '...',
    disabled = false,
    valid = $bindable(true),
    label = null,
    tall = false,
    name = undefined,
    class: className = null,
    onFocusOut = undefined,
    onEdit = undefined,
    dataTestId = undefined,
    onblur = undefined,
    oninput = undefined,
    onkeyup = undefined,
    onkeydown = undefined,
    onkeypress = undefined,
  }: Props = $props();

  let element: HTMLTextAreaElement | undefined = $state();

  export function focus() {
    element?.focus();
  }

  const hasDisplayValue = $derived(!!displayValue);

  let isEditMode = $state(!displayValue);
  $effect.pre(() => {
    isEditMode = !hasDisplayValue;
  });

  function toggleEditMode() {
    isEditMode = !isEditMode;
  }

  // Plain $effect (not .pre): focusing needs the element in the DOM.
  $effect(() => {
    if (hasDisplayValue && isEditMode && element) element.focus();
  });

  const classes = $derived(
    twMerge([
      `bg-transparent text-gray-2 border-gray-6 placeholder-gray-6 hover:border-white h-24`,
      'p-2 block border w-full rounded-sm focus:border-white outline-none dark:[color-scheme:dark] transition',
      disabled && 'bg-gray-9 text-gray-3 border-gray-9 hover:border-gray-9',
      !valid &&
        'border-error hover:border-error dark:border-error dark:hover:border-error animate-shake',
      tall && 'h-48',
      className,
      !isEditMode ? 'hover:border-gray-6 cursor-pointer' : '',
    ]),
  );
</script>

<div class="relative">
  {#if label}
    <div class="mb-1 font-mono text-gray-800 dark:text-white">{label}</div>
  {/if}
  {#if isEditMode}
    <textarea
      bind:this={element}
      {disabled}
      class={classes}
      {name}
      {onblur}
      {oninput}
      {onkeyup}
      {onkeydown}
      {onkeypress}
      onfocusout={() => {
        if (hasDisplayValue) toggleEditMode();
        onFocusOut?.();
      }}
      onfocusin={() => {
        onEdit?.();
      }}
      {placeholder}
      bind:value
      {rows}
      data-testid={dataTestId}
    ></textarea>
  {:else}
    <textarea
      class={classes}
      {placeholder}
      onclick={toggleEditMode}
      value={displayValue}
      {rows}
      data-testid={dataTestId}
    ></textarea>
  {/if}
</div>
